import React, { useState, useEffect } from 'react';
import './App.css';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

// Backend URL configuration
const BACKEND_URL = '/api';  // API endpoints will be prefixed with /api

// Interface for script metadata
interface ScriptMetadata {
  name: string;
  description: string;
  category: string;
  protocols: string[];
}

// Interface for script response
interface ScriptResponse {
  metadata: ScriptMetadata;
  script: string;
}

// Auth interfaces
interface AuthState {
  isAuthenticated: boolean;
  userId: string | null;
  username: string | null;
  email: string | null;
  cell: string | null;
  expiresIn: number | null;
}

// Placeholder text for the textarea based on script type
const MONITORING_PLACEHOLDER =
  'Describe in plain language the monitoring script you would like to create, mentioning: device and/or service, protocol used for the communication (SSH, HTTP, etc.), link to possible device/service public APIs, metrics to extract, etc.';
const BACKUP_PLACEHOLDER =
  'Describe in plain language the backup script you would like to create, mentioning: device brand/model, communication protocol (SSH,TFTP,HTTP, etc.), link to possible APIs we can use, etc.';

// Model selector component
function ModelSelector({ selectedModel, onModelChange }: { selectedModel: string; onModelChange: (model: string) => void }) {
  const availableModels = [
    { value: 'o4-mini', label: 'o4-mini (suggested)' },
    { value: 'claude-sonnet-4-5', label: 'claude-sonnet-4-5' },
    { value: 'gpt-5', label: 'gpt-5' },
    { value: 'gpt-5-mini', label: 'gpt-5-mini' },
    { value: 'claude-opus-4-20250514', label: 'claude-opus-4-20250514' },
    { value: 'claude-sonnet-4-20250514', label: 'claude-sonnet-4-20250514' }
  ];

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <label style={{ fontSize: '1rem', color: '#333', fontWeight: '600' }}>Model:</label>
      <select
        value={selectedModel}
        onChange={(e) => onModelChange(e.target.value)}
        style={{
          padding: '0.6rem 0.8rem',
          borderRadius: '6px',
          border: '2px solid #007bff',
          backgroundColor: 'white',
          fontSize: '1rem',
          minWidth: '220px',
          fontWeight: '500',
          color: '#333',
          cursor: 'pointer',
          boxShadow: '0 2px 4px rgba(0, 123, 255, 0.1)'
        }}
      >
        {availableModels.map((model) => (
          <option key={model.value} value={model.value}>
            {model.label}
          </option>
        ))}
      </select>
    </div>
  );
}

// Login component
function LoginScreen({ onLogin }: { onLogin: (auth: AuthState) => void }) {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async () => {
    setError('');
    setIsLoading(true);

    // Create an AbortController for timeout handling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30 * 1000); // 30 seconds timeout for login

    try {
      const response = await fetch(`${BACKEND_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important: include credentials for cookies
        signal: controller.signal,
      });

      // Clear the timeout since the request completed
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        // Redirect to OIDC provider for authentication
        window.location.href = data.auth_url;
      } else {
        setError('Failed to initiate login');
      }
    } catch (err) {
      // Clear the timeout in case of error
      clearTimeout(timeoutId);
      
      if (err instanceof Error && err.name === 'AbortError') {
        setError('Login request timed out. Please try again.');
      } else {
        setError('Failed to connect to authentication service');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container" style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5'
    }}>
      <div className="login-card" style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        width: '100%',
        maxWidth: '400px',
        textAlign: 'center'
      }}>
        <h1 style={{ textAlign: 'center', marginBottom: '2rem' }}>
          Domotz <span className="brand-accent">ScriptGenie</span>
        </h1>
        <h2 style={{ textAlign: 'center', marginBottom: '2rem' }}>Welcome</h2>

        {error && (
          <div style={{
            color: 'red',
            marginBottom: '1rem',
            textAlign: 'center',
            fontSize: '0.9rem'
          }}>
            {error}
          </div>
        )}

        <button
          onClick={handleLogin}
          disabled={isLoading}
          style={{
            width: '100%',
            padding: '0.75rem',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '1rem',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.7 : 1
          }}
        >
          {isLoading ? 'Redirecting...' : 'Sign in with Domotz'}
        </button>

        <div style={{
          marginTop: '2rem',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          fontSize: '0.9rem',
          color: '#666'
        }}>
          You will be redirected to Domotz login page to authenticate securely.
        </div>
      </div>
    </div>
  );
}

function App() {
  // Authentication state
  const [auth, setAuth] = useState<AuthState>({
    isAuthenticated: false,
    userId: null,
    username: null,
    email: null,
    cell: null,
    expiresIn: null,
  });

  // State variables to manage the application
  const [selectedType, setSelectedType] = useState<string | null>('monitoring');
  const [selectedModel, setSelectedModel] = useState<string>('o4-mini'); // Default model
  const [createError, setCreateError] = useState('');
  const [showSelectDialog, setShowSelectDialog] = useState(false);
  const [showPasteArea, setShowPasteArea] = useState(false);
  const [pastedScript, setPastedScript] = useState('');
  const [messageText, setMessageText] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [chatHistory, setChatHistory] = useState<Array<{ role: 'user' | 'assistant', content: string }>>([]);
  const [conversationId, setConversationId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [showScriptSources, setShowScriptSources] = useState(false);
  const [scriptSources, setScriptSources] = useState<ScriptResponse[]>([]);
  const [currentScriptMetadata, setCurrentScriptMetadata] = useState<ScriptMetadata | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [checkpoints, setCheckpoints] = useState<Array<any>>([]);
  const [selectedCheckpoints, setSelectedCheckpoints] = useState<string[]>([]);

  // Check for existing auth on mount
  useEffect(() => {
    // First check if we're coming back from OIDC callback
    const urlParams = new URLSearchParams(window.location.search);
    const path = window.location.pathname;
    
    // Handle both /auth-callback and /app/auth-callback paths
    if (path === '/auth-callback' || path === '/app/auth-callback') {
      // Check for error
      const error = urlParams.get('error');
      if (error === 'access_denied') {
        // Clear any existing auth state
        setAuth({
          isAuthenticated: false,
          userId: null,
          username: null,
          email: null,
          cell: null,
          expiresIn: null,
        });
        // Clean URL and return to home
        window.history.replaceState({}, document.title, '/app');
        return;
      }

      // Extract auth data from URL parameters
      const authData = {
        user_id: urlParams.get('user_id'),
        username: urlParams.get('username'),
        email: urlParams.get('email'),
        cell: urlParams.get('cell'),
        expires_in: urlParams.get('expires_in')
      };

      if (authData.email) {
        // Set auth state
        setAuth({
          isAuthenticated: true,
          userId: authData.user_id,
          username: authData.username,
          email: authData.email,
          cell: authData.cell,
          expiresIn: authData.expires_in ? parseInt(authData.expires_in) : null,
        });

        // Clean URL
        window.history.replaceState({}, document.title, '/app');
        return;
      }
    }

    // Otherwise, validate session
    // Add delay if we're coming from OAuth callback to prevent race conditions
    const isOAuthCallback = path === '/auth-callback' || path === '/app/auth-callback';
    const delay = isOAuthCallback ? 2000 : 0; // 2 second delay for OAuth callback to prevent race conditions
    validateSession(0, delay);

    // Set up periodic session validation every 30 minutes
    const intervalId = setInterval(() => {
      if (auth.isAuthenticated) {
        validateSession();
      }
    }, 30 * 60 * 1000); // 30 minutes

    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, [auth.isAuthenticated]);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    const chatHistoryColumn = document.querySelector('.chat-history-column');
    if (chatHistoryColumn) {
      chatHistoryColumn.scrollTop = chatHistoryColumn.scrollHeight;
    }
  }, [chatHistory]);

  // Validate session with backend
  const validateSession = async (retryCount = 0, initialDelay = 0) => {
    const maxRetries = 3; // Increased retries for better reliability
    const timeoutDuration = 30 * 1000; // 30 seconds timeout for token validation
    
    // Add initial delay to prevent race conditions after OAuth callback
    if (initialDelay > 0) {
      console.log(`Waiting ${initialDelay}ms before session validation to prevent race conditions...`);
      await new Promise(resolve => setTimeout(resolve, initialDelay));
    }
    
    // Create an AbortController for timeout handling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutDuration);

    try {
      const response = await fetch(`${BACKEND_URL}/validate-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important: include credentials for cookies
        signal: controller.signal,
      });

      // Clear the timeout since the request completed
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        setAuth({
          isAuthenticated: true,
          userId: data.user.sub,
          username: data.user.name || data.user.email,
          email: data.user.email,
          cell: data.user.cell || 'EU',
          expiresIn: null, // Session expiry is handled by cookies
        });
        console.log('Session validation successful');
      } else if (response.status === 401) {
        // Session invalid, clear auth
        setAuth({
          isAuthenticated: false,
          userId: null,
          username: null,
          email: null,
          cell: null,
          expiresIn: null,
        });
      } else if (retryCount < maxRetries) {
        // Server error, retry with exponential backoff
        const delay = Math.min(1000 * Math.pow(2, retryCount), 5000); // Cap at 5 seconds
        console.log(`Session validation failed with status ${response.status}, retrying in ${delay}ms... (${retryCount + 1}/${maxRetries})`);
        setTimeout(() => validateSession(retryCount + 1, 0), delay);
        return;
      } else {
        // Max retries reached, clear auth
        setAuth({
          isAuthenticated: false,
          userId: null,
          username: null,
          email: null,
          cell: null,
          expiresIn: null,
        });
      }
    } catch (err) {
      // Clear the timeout in case of error
      clearTimeout(timeoutId);
      
      if (err instanceof Error && err.name === 'AbortError') {
        console.log('Session validation timed out');
      } else {
        console.log('Session validation error:', err);
      }
      
      if (retryCount < maxRetries) {
        // Network error, retry with exponential backoff
        const delay = Math.min(1000 * Math.pow(2, retryCount), 5000); // Cap at 5 seconds
        console.log(`Session validation failed due to network error, retrying in ${delay}ms... (${retryCount + 1}/${maxRetries})`);
        setTimeout(() => validateSession(retryCount + 1, 0), delay);
        return;
      }
      
      // Max retries reached or other error, clear auth
      setAuth({
        isAuthenticated: false,
        userId: null,
        username: null,
        email: null,
        cell: null,
        expiresIn: null,
      });
    }
  };

  // Logout function
  const handleLogout = async () => {
    // Create an AbortController for timeout handling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10 * 1000); // 10 seconds timeout for logout

    try {
      const response = await fetch(`${BACKEND_URL}/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important: include credentials for cookies
        signal: controller.signal,
      });

      // Clear the timeout since the request completed
      clearTimeout(timeoutId);

      if (!response.ok) {
        console.error('Logout failed:', response.statusText);
      }
    } catch (err) {
      // Clear the timeout in case of error
      clearTimeout(timeoutId);
      
      if (err instanceof Error && err.name === 'AbortError') {
        console.error('Logout request timed out');
      } else {
        console.error('Logout error:', err);
      }
    }

    // Clear auth state
    setAuth({
      isAuthenticated: false,
      userId: null,
      username: null,
      email: null,
      cell: null,
      expiresIn: null,
    });

    // Reset app state
    setShowChat(false);
    setSelectedType('monitoring');
    setMessageText('');
    setChatHistory([]);
    setCurrentScriptMetadata(null);
    setConversationId(''); // Clear conversation ID
    setShowScriptSources(false);
    setShowPasteArea(false);
    setShowSelectDialog(false);
    setShowHistory(false);
    setCheckpoints([]);
    setSelectedCheckpoints([]);
  };

  // Loading spinner component
  const LoadingSpinner = () => (
    <div className="loading-spinner">
      <div className="spinner"></div>
      <span>Waiting for response...</span>
    </div>
  );

  // Loading overlay component
  const LoadingOverlay = ({ isReloading }: { isReloading?: boolean }) => (
    <div className="loading-overlay">
      <div className="loading-spinner">
        <div className="spinner"></div>
        <span>{isReloading ? "It might take a while..." : "Please wait..."}</span>
      </div>
    </div>
  );

  // Script metadata display component
  const ScriptMetadataDisplay = ({ metadata }: { metadata: ScriptMetadata }) => (
    <div className="script-metadata">
      <h3>{metadata.name}</h3>
      <p className="metadata-description">{metadata.description}</p>
      <div className="metadata-details">
        <div className="metadata-item">
          <span className="metadata-label">Category</span>
          <span className="metadata-value">{metadata.category}</span>
        </div>
        <div className="metadata-item">
          <span className="metadata-label">Protocols</span>
          <span className="metadata-value">{metadata.protocols.join(', ')}</span>
        </div>

      </div>
    </div>
  );

  // Helper function to clean JavaScript code block markers from messages
  const cleanJavaScriptMarkers = (content: string): string => {
    let cleaned = content;
    
    // Remove ```javascript at the beginning
    if (cleaned.startsWith('```javascript')) {
      cleaned = cleaned.substring(13); // Remove '```javascript'
    } else if (cleaned.startsWith('```')) {
      cleaned = cleaned.substring(3); // Remove '```'
    }
    
    // Remove ``` at the end
    if (cleaned.endsWith('```')) {
      cleaned = cleaned.substring(0, cleaned.length - 3); // Remove '```'
    }
    
    // Trim any extra whitespace
    return cleaned.trim();
  };

  // Helper to send messages using the new simplified endpoint
  const sendMessage = async (message: string, conversationId: string) => {
    const payload = {
      message: message,
      conversation_id: conversationId,
      script_type: selectedType || 'monitoring',
      model_name: selectedModel
    };

    // Create an AbortController for timeout handling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 6 * 60 * 1000); // 6 minutes timeout - longer than backend
    
    // Show progress indicator for long requests
    const progressTimeout = setTimeout(() => {
      if (isLoading) {
        console.log('Request is taking longer than expected... This is normal for complex requests.');
      }
    }, 30000); // Show message after 30 seconds

    try {
      const response = await fetch(`${BACKEND_URL}/send_message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important: include credentials for cookies
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      // Clear the timeouts since the request completed
      clearTimeout(timeoutId);
      clearTimeout(progressTimeout);

      if (!response.ok) {
        if (response.status === 401) {
          handleLogout();
          throw new Error('Unauthorized');
        }
        throw new Error('Failed to send message');
      }

      return response.json();
    } catch (error) {
      // Clear the timeouts in case of error
      clearTimeout(timeoutId);
      clearTimeout(progressTimeout);
      
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timed out after 6 minutes. Please try again with a shorter message or different model.');
      }
      throw error;
    }
  };

  // Helper to load checkpoints from localStorage
  function loadCheckpoints() {
    const data = JSON.parse(localStorage.getItem('scriptCheckpoints') || '[]');
    setCheckpoints(data.reverse()); // newest first
  }

  // Call this when you want to show the modal
  function openHistory() {
    loadCheckpoints();
    setShowHistory(true);
  }

  // Restore a checkpoint
  async function restoreCheckpoint(checkpoint: any) {
    setIsLoading(true);
    try {
      // Debug: check what's in the checkpoint
      console.log('Restoring checkpoint:', checkpoint);

      // Use the checkpoint's conversation ID directly
      const checkpointConversationId = checkpoint.conversationId || '';

      if (!checkpointConversationId) {
        console.error('No conversation ID in checkpoint!');
        // Generate a new one if missing
        const newConversationId = Math.random().toString(36).substring(2, 18);
        setConversationId(newConversationId);

        const message = `This is the script you should work on. Await for new instructions. Simply reply with "Script loaded!" and nothing else. ${checkpoint.script}`;
        await sendMessage(message, newConversationId);
      } else {
        // Set the conversation ID in state for future messages
        setConversationId(checkpointConversationId);

        const message = `This is the script you should work on. Await for new instructions. Simply reply with "Script loaded!" and nothing else. ${checkpoint.script}`;
        await sendMessage(message, checkpointConversationId);
      }

      // Set the script type from checkpoint
      if (checkpoint.scriptType) {
        setSelectedType(checkpoint.scriptType === 'script_with_type' ? 'backup' : 'monitoring');
      }

      setChatHistory(checkpoint.chatHistory);
      setShowHistory(false);
      setShowChat(true);
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  }

  // Add this helper function inside your App component, before the handlers
  function saveCheckpoint(
    script: string,
    chatHistory: Array<{ role: 'user' | 'assistant'; content: string }>,
    lastUserMessage: string,
    currentConversationId: string,
    scriptType: string // Add script type parameter
  ) {
    const checkpoints = JSON.parse(localStorage.getItem('scriptCheckpoints') || '[]');
    checkpoints.push({
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
      script,
      chatHistory,
      lastUserMessage,
      conversationId: currentConversationId,
      scriptType: scriptType // Save script type
    });
    localStorage.setItem('scriptCheckpoints', JSON.stringify(checkpoints));
  }

  // Helper to check if there are checkpoints in localStorage
  function hasCheckpoints() {
    const data = JSON.parse(localStorage.getItem('scriptCheckpoints') || '[]');
    return data.length > 0;
  }

  // Handler for creating a new script
  const handleCreateScript = async () => {
    setCreateError('');

    if (!messageText.trim()) {
      setCreateError('Please enter a description for your script');
      return;
    }

    setIsLoading(true);
    try {
      // Only generate new conversation ID if we don't have one
      let currentConversationId = conversationId;
      if (!currentConversationId) {
        currentConversationId = Math.random().toString(36).substring(2, 18);
        setConversationId(currentConversationId);
      }

      const responseData = await sendMessage(messageText, currentConversationId);

      if (responseData.sources && Array.isArray(responseData.sources)) {
        // Handle the list of scripts with their metadata
        const scripts = responseData.sources.map((source: any) => {
          if (Array.isArray(source) && source.length === 2) {
            const [script, metadata] = source;
            return { script, metadata };
          }
          return null;
        }).filter(Boolean) as ScriptResponse[];

        // Keep all scripts as received - no filtering or grouping
        setScriptSources(scripts);
        if (scripts.length > 0) {
          setCurrentScriptMetadata(scripts[0].metadata);
        }
        setShowScriptSources(true);
        // Store the response message for later use
        const newHistory: Array<{ role: 'user' | 'assistant'; content: string }> = [
          ...chatHistory,
          { role: 'user', content: messageText },
          { role: 'assistant', content: cleanJavaScriptMarkers(responseData.message || 'Here are some scripts you can use:') }
        ];
        const latestScript = newHistory.filter(msg => msg.role === 'assistant').slice(-1)[0]?.content || '';
        const lastUserMsg = newHistory.filter(msg => msg.role === 'user').slice(-1)[0]?.content || '';
        saveCheckpoint(latestScript, newHistory, lastUserMsg, currentConversationId, selectedType || 'monitoring');
        setChatHistory(newHistory);
      } else {
        const newHistoryElse: Array<{ role: 'user' | 'assistant'; content: string }> = [
          ...chatHistory,
          { role: 'user', content: messageText },
          { role: 'assistant', content: cleanJavaScriptMarkers(responseData.answer || responseData.message || 'No response received') }
        ];
        const latestScriptElse = newHistoryElse.filter(msg => msg.role === 'assistant').slice(-1)[0]?.content || '';
        const lastUserMsgElse = newHistoryElse.filter(msg => msg.role === 'user').slice(-1)[0]?.content || '';
        saveCheckpoint(latestScriptElse, newHistoryElse, lastUserMsgElse, currentConversationId, selectedType || 'monitoring');
        setChatHistory(newHistoryElse);
        setMessageText('');
        setShowChat(true);
      }
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  // Handler for sending a new message in chat
  const handleSendMessage = async () => {
    if (!messageText.trim()) return;

    setIsLoading(true);
    try {
      const responseData = await sendMessage(messageText, conversationId);

      const newHistoryMsg: Array<{ role: 'user' | 'assistant'; content: string }> = [
        ...chatHistory,
        { role: 'user', content: messageText },
        { role: 'assistant', content: cleanJavaScriptMarkers(responseData.answer || responseData.message || 'No response received') }
      ];
      const latestScriptMsg = newHistoryMsg.filter(msg => msg.role === 'assistant').slice(-1)[0]?.content || '';
      const lastUserMsgMsg = newHistoryMsg.filter(msg => msg.role === 'user').slice(-1)[0]?.content || '';
      saveCheckpoint(latestScriptMsg, newHistoryMsg, lastUserMsgMsg, conversationId, selectedType || 'monitoring');
      setChatHistory(newHistoryMsg);

      setMessageText('');
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  // Handler for opening the script selection dialog
  const handleSelectScript = () => {
    setShowSelectDialog(true);
  };

  // Handler for file upload
  const handleUploadFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.js';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        setIsLoading(true);
        try {
          const content = await file.text();
          // Only generate new conversation ID if we don't have one
          let currentConversationId = conversationId;
          if (!currentConversationId) {
            currentConversationId = Math.random().toString(36).substr(2, 16);
            setConversationId(currentConversationId);
          }

          const scriptWithComment = `//Loaded script\n${content}`;
          const messageTextUpload = `This is the script you will have to configure. For now, do nothing and await for my instructions. Simply reply with 'Script loaded!' and nothing else.\n\n${scriptWithComment}`;

          // Show the script in the editor and bot message on the left
          setChatHistory([
            { role: 'assistant', content: "This is the script you will have to configure. For now, do nothing and await for my instructions. Simply reply with 'Script loaded!' and nothing else." },
            { role: 'assistant', content: scriptWithComment }
          ]);

          const responseData = await sendMessage(messageTextUpload, currentConversationId);

          // Update the assistant's response while keeping the script in the editor
          setChatHistory(prev => [
            { role: 'assistant', content: cleanJavaScriptMarkers(responseData.answer || responseData.message || 'No response received') },
            prev[1] // Keep the script in the editor
          ]);

          // Close the dialog and show chat interface
          setShowSelectDialog(false);
          setShowChat(true);
        } catch (error) {
          setCreateError(error instanceof Error ? error.message : 'An error occurred');
        } finally {
          setIsLoading(false);
        }
      }
    };
    input.click();
    setShowSelectDialog(false);
  };

  // Handler for opening the paste area
  const handleCopyPaste = () => {
    setShowPasteArea(true);
    setShowSelectDialog(false);
    setShowScriptSources(false);
    sessionStorage.removeItem('fromScriptSelection');
  };

  // Handler for submitting pasted script
  const handlePasteSubmit = async () => {
    if (!pastedScript.trim()) return;

    setIsLoading(true);
    try {
      sessionStorage.removeItem('fromScriptSelection');
      // Only generate new conversation ID if we don't have one
      let currentConversationId = conversationId;
      if (!currentConversationId) {
        currentConversationId = Math.random().toString(36).substr(2, 16);
        setConversationId(currentConversationId);
      }

      const scriptWithComment = `//Loaded script\n${pastedScript}`;
      const messageTextPaste = `This is the script you will have to configure. For now, do nothing and await for my instructions. Simply reply with 'Script loaded!' and nothing else.\n\n${scriptWithComment}`;

      setChatHistory([
        { role: 'assistant', content: "This is the script you will have to configure. For now, do nothing and await for my instructions. Simply reply with 'Script loaded!' and nothing else." },
        { role: 'assistant', content: scriptWithComment }
      ]);

      const responseData = await sendMessage(messageTextPaste, currentConversationId);

      setChatHistory(prev => [
        { role: 'assistant', content: cleanJavaScriptMarkers(responseData.answer || responseData.message || 'No response received') },
        prev[1]
      ]);

      setPastedScript('');
      setShowPasteArea(false);
      setShowSelectDialog(false);
      setMessageText('');
      setShowChat(true);
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  // Handler for selecting an existing script
  const handleSelectExistingScript = (scriptIndex: number) => {
    const scriptData = scriptSources[scriptIndex];
    if (scriptData) {
      setPastedScript(scriptData.script);
      setCurrentScriptMetadata(scriptData.metadata);
      setShowScriptSources(false);
      setShowPasteArea(true);
      sessionStorage.setItem('fromScriptSelection', 'true');
    }
  };

  // Handler to toggle selection of a checkpoint
  const handleCheckpointSelect = (id: string) => {
    setSelectedCheckpoints((prev) =>
      prev.includes(id) ? prev.filter((cpId) => cpId !== id) : [...prev, id]
    );
  };

  // Handler to select/deselect all checkpoints
  const handleSelectAllCheckpoints = () => {
    if (selectedCheckpoints.length === checkpoints.length) {
      setSelectedCheckpoints([]);
    } else {
      setSelectedCheckpoints(checkpoints.map((cp) => cp.id));
    }
  };

  // Handler to delete selected checkpoints
  const handleDeleteSelectedCheckpoints = () => {
    const newCheckpoints = checkpoints.filter((cp) => !selectedCheckpoints.includes(cp.id));
    localStorage.setItem('scriptCheckpoints', JSON.stringify([...newCheckpoints].reverse()));
    setCheckpoints(newCheckpoints);
    setSelectedCheckpoints([]);
  };

  // Handler to clean all history
  const handleCleanAllHistory = () => {
    localStorage.removeItem('scriptCheckpoints');
    setCheckpoints([]);
    setSelectedCheckpoints([]);
  };

  // If not authenticated, show login screen
  if (!auth.isAuthenticated) {
    return <LoginScreen onLogin={(authState) => {
      setAuth(authState);
    }} />;
  }

  if (showChat) {
    return (
      <div className="chat-container">
        <header className="landing-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
            <span className="brand" onClick={() => {
              setShowChat(false);
              setSelectedType('monitoring');
              setMessageText('');
              setChatHistory([]);
              setCurrentScriptMetadata(null);
              setConversationId(''); // Clear conversation ID when going back
            }}>Domotz <span className="brand-accent">ScriptGenie</span></span>
            <ModelSelector selectedModel={selectedModel} onModelChange={setSelectedModel} />
          </div>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <span style={{ color: '#666', fontSize: '0.9rem' }}>
              Logged as: {auth.email}
            </span>
            <button className="history-btn" onClick={openHistory}>Script Refinement History</button>
            <button className="history-btn" onClick={handleLogout}>Logout</button>
          </div>
        </header>

        <div className="chat-split-view">
          <div className="chat-history-column">
            {currentScriptMetadata && showScriptSources && <ScriptMetadataDisplay metadata={currentScriptMetadata} />}
            <div className="chat-message assistant welcome-message">
              <div className="message-content">I am Domotz ScriptGenie, how can I help refine your script?</div>
            </div>
            {chatHistory
              .filter(message => {
                const isAssistant = message.role === 'assistant';
                const isScriptMessage = message.content.startsWith('/');
                return message.role === 'user' || (isAssistant && !isScriptMessage);
              })
              .map((message, index, filteredMessages) => {
                // Find the latest user message in the entire chat history
                const userMessages = chatHistory.filter(msg => msg.role === 'user');
                const latestUserMessage = userMessages[userMessages.length - 1];
                const isLatestUserMessage = message.role === 'user' && message === latestUserMessage;
                
                // Check if there's a script message that was generated as a response to the latest user message
                // This means there should be a script message after the latest user message
                const latestUserMessageIndex = chatHistory.indexOf(latestUserMessage);
                const hasNewScript = chatHistory.some((msg, msgIndex) => 
                  msg.role === 'assistant' && 
                  msg.content.startsWith('/') && 
                  msgIndex > latestUserMessageIndex
                );
                
                // Check if this is not the first script (there should be more than one script in total)
                const scriptCount = chatHistory.filter(msg => 
                  msg.role === 'assistant' && msg.content.startsWith('/')
                ).length;
                const isNotFirstScript = scriptCount > 1;
                
                // Debug logging
                if (isLatestUserMessage) {
                  console.log('Latest user message found:', {
                    message: message.content,
                    hasNewScript,
                    isNotFirstScript,
                    scriptCount,
                    latestUserMessageIndex,
                    chatHistoryLength: chatHistory.length
                  });
                }
                
                return (
                  <div key={`${message.role}-${index}`} className={`chat-message ${message.role}`}>
                    <div className="message-content">{message.content}</div>
                    {isLatestUserMessage && hasNewScript && isNotFirstScript && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <button
                          className="explain-changes-btn"
                          onClick={async () => {
                            const explainMessage = "Explain the changes compared to the previous version of the script.";
                            
                            setIsLoading(true);
                            try {
                              const responseData = await sendMessage(explainMessage, conversationId);

                              const newHistoryMsg: Array<{ role: 'user' | 'assistant'; content: string }> = [
                                ...chatHistory,
                                { role: 'user', content: explainMessage },
                                { role: 'assistant', content: cleanJavaScriptMarkers(responseData.answer || responseData.message || 'No response received') }
                              ];
                              const latestScriptMsg = newHistoryMsg.filter(msg => msg.role === 'assistant').slice(-1)[0]?.content || '';
                              const lastUserMsgMsg = newHistoryMsg.filter(msg => msg.role === 'user').slice(-1)[0]?.content || '';
                              saveCheckpoint(latestScriptMsg, newHistoryMsg, lastUserMsgMsg, conversationId, selectedType || 'monitoring');
                              setChatHistory(newHistoryMsg);
                            } catch (error) {
                              setCreateError(error instanceof Error ? error.message : 'An error occurred');
                            } finally {
                              setIsLoading(false);
                            }
                          }}
                          style={{
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '0.5rem 1rem',
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                            marginTop: '0.5rem'
                          }}
                        >
                          Explain the changes
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>

          <div className="terminal-column">
            <div className="terminal-header">
              <span className="terminal-title">Script Editor</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className="copy-button"
                  onClick={async () => {
                    const explainMessage = "Explain how the script works and its parameters. If this script is a modification of a previous one, please describe the changes.";
                    
                    setIsLoading(true);
                    try {
                      const responseData = await sendMessage(explainMessage, conversationId);

                      const newHistoryMsg: Array<{ role: 'user' | 'assistant'; content: string }> = [
                        ...chatHistory,
                        { role: 'user', content: explainMessage },
                        { role: 'assistant', content: cleanJavaScriptMarkers(responseData.answer || responseData.message || 'No response received') }
                      ];
                      const latestScriptMsg = newHistoryMsg.filter(msg => msg.role === 'assistant').slice(-1)[0]?.content || '';
                      const lastUserMsgMsg = newHistoryMsg.filter(msg => msg.role === 'user').slice(-1)[0]?.content || '';
                      saveCheckpoint(latestScriptMsg, newHistoryMsg, lastUserMsgMsg, conversationId, selectedType || 'monitoring');
                      setChatHistory(newHistoryMsg);
                    } catch (error) {
                      console.error('Error sending explain message:', error);
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                >
                  Explain
                </button>
                <button
                  className="copy-button"
                  onClick={async () => {
                    try {
                      const scriptContent = chatHistory
                        .filter(message => {
                          const isAssistant = message.role === 'assistant';
                          const isScriptMessage = message.content.startsWith('/');
                          return isAssistant && isScriptMessage;
                        })
                        .slice(-1)[0]?.content || '';
                      await navigator.clipboard.writeText(scriptContent);
                    } catch (error) {
                      // Fallback method
                      try {
                        const scriptContent = chatHistory
                          .filter(message => {
                            const isAssistant = message.role === 'assistant';
                            const isScriptMessage = message.content.startsWith('/');
                            return isAssistant && isScriptMessage;
                          })
                          .slice(-1)[0]?.content || '';
                        const textarea = document.createElement('textarea');
                        textarea.value = scriptContent;
                        document.body.appendChild(textarea);
                        textarea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textarea);
                      } catch (fallbackError) {
                        console.error('Failed to copy text:', fallbackError);
                        alert('Failed to copy text to clipboard. Please try again.');
                      }
                    }
                  }}
                >
                  Copy
                </button>
              </div>
            </div>
            <div className="terminal-content">
              <SyntaxHighlighter
                language="javascript"
                style={vscDarkPlus}
                customStyle={{
                  margin: 0,
                  padding: '1rem',
                  height: '100%',
                  background: '#1e1e1e',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
                }}
                showLineNumbers={true}
                wrapLines={true}
                wrapLongLines={true}
              >
                {(() => {
                  const scriptMessages = chatHistory.filter(message => {
                    const isAssistant = message.role === 'assistant';
                    const isScriptMessage = message.content.startsWith('/');
                    return isAssistant && isScriptMessage;
                  });
                  const content = scriptMessages.slice(-1)[0]?.content || '';
                  return content;
                })()}
              </SyntaxHighlighter>
            </div>
          </div>
        </div>

        <div className="chat-input-container">
          {isLoading && <LoadingOverlay isReloading={showHistory} />}
          <textarea
            className="chat-input"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="Type your message..."
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            disabled={isLoading}
          />
          <button
            className="send-button"
            onClick={handleSendMessage}
            disabled={isLoading}
          >
            Send
          </button>
        </div>

        {showHistory && (
          <div className="modal-backdrop" onClick={() => setShowHistory(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setShowHistory(false)} aria-label="Close dialog">&times;</button>
              <h2>Script Refinement History</h2>
              {checkpoints.length === 0 && <div>No checkpoints saved yet.</div>}
              {checkpoints.length > 0 && (
                <div style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={selectedCheckpoints.length === checkpoints.length && checkpoints.length > 0}
                      onChange={handleSelectAllCheckpoints}
                    />
                    Select All
                  </label>
                  <button
                    className="test-btn"
                    onClick={handleDeleteSelectedCheckpoints}
                    disabled={selectedCheckpoints.length === 0}
                  >
                    Delete Selected
                  </button>
                  <button
                    className="test-btn"
                    onClick={handleCleanAllHistory}
                    disabled={checkpoints.length === 0}
                  >
                    Clean All History
                  </button>
                </div>
              )}
              <ul style={{ maxHeight: '60vh', overflowY: 'auto', padding: 0 }}>
                {checkpoints.map((cp, idx) => (
                  <li key={cp.id} style={{ marginBottom: '1rem', listStyle: 'none', borderBottom: '1px solid #eee', paddingBottom: '1rem', display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                    <input
                      type="checkbox"
                      checked={selectedCheckpoints.includes(cp.id)}
                      onChange={() => handleCheckpointSelect(cp.id)}
                      style={{ marginTop: '0.3rem' }}
                    />
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
                        <div><b>Saved:</b> {new Date(cp.createdAt).toLocaleString()}</div>
                        <div><b>Last user message:</b> {cp.lastUserMessage}</div>
                      </div>
                      <button className="test-btn" onClick={() => restoreCheckpoint(cp)}>Restore</button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="landing-root">
      {isLoading && <LoadingOverlay isReloading={showHistory} />}
      {/* Header with brand name */}
      <header className="landing-header" style={{ position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
          <span className="brand" onClick={() => {
            setShowChat(false);
            setSelectedType('monitoring');
            setMessageText('');
            setChatHistory([]);
            setConversationId(''); // Clear conversation ID when going back
          }}>Domotz <span className="brand-accent">ScriptGenie</span></span>
          <ModelSelector selectedModel={selectedModel} onModelChange={setSelectedModel} />
        </div>
        <div style={{ position: 'absolute', right: 32, top: 24, display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <span style={{ color: '#666', fontSize: '0.9rem' }}>
            Logged as: {auth.email}
          </span>
          {hasCheckpoints() && (
            <button
              className="history-btn"
              onClick={openHistory}
            >
              Script Refinement History
            </button>
          )}
          <button
            className="history-btn"
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>
      </header>

      {/* Main content area */}
      <main className="landing-main">
        <h1>
          Create your <span className="highlight">Domotz Integration</span> script
        </h1>
        <p className="subtitle">
          Select the type of script you would like to create using the buttons below
        </p>

        {/* Script type selection buttons */}
        <div className="button-group">
          <button
            className={`type-btn${selectedType === 'monitoring' ? ' selected' : ''}`}
            onClick={() => setSelectedType('monitoring')}
          >
            Monitoring Script
          </button>
          <button
            className={`type-btn${selectedType === 'backup' ? ' selected' : ''}`}
            onClick={() => setSelectedType('backup')}
          >
            Configuration Backup Script
          </button>
        </div>

        <div className="main-content">
        {/* Script description input area */}
          <div className="create-script-section">
        <div className="input-card">
          <textarea
            className="desc-textarea"
            placeholder={
              selectedType === 'monitoring'
                ? MONITORING_PLACEHOLDER
                : selectedType === 'backup'
                ? BACKUP_PLACEHOLDER
                : 'Select a script type and describe what you want to create...'
            }
                disabled={!selectedType || isLoading}
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
          />
          {createError && <div className="create-error">{createError}</div>}
              <button
                className="create-btn"
                onClick={handleCreateScript}
                disabled={isLoading}
              >
                Create Script
              </button>
            </div>
        </div>

        {/* Customize existing script section */}
        <div className="customize-section">
            <h3>Or, customize your script</h3>
            <p className="subtitle" style={{ fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              Upload or paste an existing script to modify it
            </p>
          <button className="upload-btn" onClick={handleSelectScript}>Select Script</button>
          </div>
        </div>
      </main>

      {/* Script selection dialog */}
      {showSelectDialog && (
        <div className="modal-backdrop" onClick={() => setShowSelectDialog(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowSelectDialog(false)} aria-label="Close dialog">&times;</button>
            <h2>Select Script</h2>
            <button className="modal-choice-btn" onClick={handleUploadFile}>Upload File</button>
            <button className="modal-choice-btn" onClick={handleCopyPaste}>Copy and Paste Script</button>
          </div>
        </div>
      )}

      {/* Script paste area */}
      {showPasteArea && (
        <div className="modal-backdrop" onClick={() => {
          setShowPasteArea(false);
          // Show script sources if we came from script selection
          if (sessionStorage.getItem('fromScriptSelection') === 'true') {
            setShowScriptSources(true);
          } else {
            setShowScriptSources(false);
          }
        }}>
          <div className="modal paste-modal" onClick={e => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={() => {
                setShowPasteArea(false);
                // Show script sources if we came from script selection
                if (sessionStorage.getItem('fromScriptSelection') === 'true') {
                  setShowScriptSources(true);
                } else {
                  setShowScriptSources(false);
                }
              }}
              aria-label="Close dialog"
            >&times;</button>
            <h2>Paste Your Script</h2>
            <div className="paste-area-header">
              <button
                className="copy-button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(pastedScript);
                  } catch (error) {
                    // Fallback method
                    try {
                      const textarea = document.createElement('textarea');
                      textarea.value = pastedScript;
                      document.body.appendChild(textarea);
                      textarea.select();
                      document.execCommand('copy');
                      document.body.removeChild(textarea);
                    } catch (fallbackError) {
                      console.error('Failed to copy text:', fallbackError);
                      alert('Failed to copy text to clipboard. Please try again.');
                    }
                  }
                }}
              >
                Copy
              </button>
            </div>
            <textarea
              className="paste-textarea"
              value={pastedScript}
              onChange={e => setPastedScript(e.target.value)}
              placeholder="Paste your script here..."
            />
            <button className="modal-submit-btn" onClick={handlePasteSubmit}>Customize</button>
          </div>
        </div>
      )}

      {/* Script sources dialog */}
      {showScriptSources && (
        <div className="modal-backdrop" onClick={() => setShowScriptSources(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowScriptSources(false)} aria-label="Close dialog">&times;</button>
            <h2>We have found the following scripts which might be similar to what you would like to create, would you like to use/customize one of the following?</h2>
            <div className="script-sources-list">
              <button
                className="modal-choice-btn generate-new-btn"
                onClick={() => {
                  setShowScriptSources(false);
                  setMessageText('');
                  setShowChat(true);
                }}
              >
                No, I would like to generate one from scratch
                </button>
              {scriptSources.map((scriptData, index) => (
                <div key={index} className="script-source-item">
                  <button
                    className="modal-choice-btn script-source-btn"
                    onClick={() => handleSelectExistingScript(index)}
                  >
                    <div className="script-source-header">
                      <h3>{scriptData.metadata.name}</h3>
                      <span className="script-category">{scriptData.metadata.category}</span>
                    </div>
                    <p className="script-description">{scriptData.metadata.description}</p>
                    <div className="script-protocol">
                      <span className="protocol-label">Protocols:</span>
                      <span className="protocol-value">{scriptData.metadata.protocols.join(', ')}</span>
                    </div>

                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {showHistory && (
        <div className="modal-backdrop" onClick={() => setShowHistory(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowHistory(false)} aria-label="Close dialog">&times;</button>
            <h2>Script Refinement History</h2>
            {checkpoints.length === 0 && <div>No checkpoints saved yet.</div>}
            {checkpoints.length > 0 && (
              <div style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={selectedCheckpoints.length === checkpoints.length && checkpoints.length > 0}
                    onChange={handleSelectAllCheckpoints}
                  />
                  Select All
                </label>
                <button
                  className="test-btn"
                  onClick={handleDeleteSelectedCheckpoints}
                  disabled={selectedCheckpoints.length === 0}
                >
                  Delete Selected
                </button>
                <button
                  className="test-btn"
                  onClick={handleCleanAllHistory}
                  disabled={checkpoints.length === 0}
                >
                  Clean All History
                </button>
              </div>
            )}
            <ul style={{ maxHeight: '60vh', overflowY: 'auto', padding: 0 }}>
              {checkpoints.map((cp, idx) => (
                <li key={cp.id} style={{ marginBottom: '1rem', listStyle: 'none', borderBottom: '1px solid #eee', paddingBottom: '1rem', display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                  <input
                    type="checkbox"
                    checked={selectedCheckpoints.includes(cp.id)}
                    onChange={() => handleCheckpointSelect(cp.id)}
                    style={{ marginTop: '0.3rem' }}
                  />
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
                      <div><b>Saved:</b> {new Date(cp.createdAt).toLocaleString()}</div>
                      <div><b>Last user message:</b> {cp.lastUserMessage}</div>
                    </div>
                    <button className="test-btn" onClick={() => restoreCheckpoint(cp)}>Restore</button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;